type SnowplowFetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

interface SnowplowGzipFetchDeps {
  supportsCompression: () => boolean;
  compress: (bytes: Uint8Array) => Promise<Uint8Array>;
  encodeBase64: (bytes: Uint8Array) => string;
}

const CRC32_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let value = i;
    for (let bit = 0; bit < 8; bit += 1) {
      value = (value & 1) === 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
    }
    table[i] = value >>> 0;
  }
  return table;
})();

function getRequestMethod(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) return init.method.toUpperCase();
  if (input instanceof Request) return input.method.toUpperCase();
  return 'GET';
}

async function bodyToUint8Array(body: BodyInit | null | undefined): Promise<Uint8Array | null> {
  if (!body) return null;

  if (typeof body === 'string') {
    return new TextEncoder().encode(body);
  }

  if (body instanceof URLSearchParams) {
    return new TextEncoder().encode(body.toString());
  }

  if (body instanceof Blob) {
    return new Uint8Array(await body.arrayBuffer());
  }

  if (body instanceof ArrayBuffer) {
    return new Uint8Array(body);
  }

  if (ArrayBuffer.isView(body)) {
    return new Uint8Array(body.buffer, body.byteOffset, body.byteLength);
  }

  return null;
}

function mergeHeaders(input: RequestInfo | URL, init?: RequestInit): Headers {
  const headers = new Headers(input instanceof Request ? input.headers : undefined);
  const initHeaders = new Headers(init?.headers);
  initHeaders.forEach((value, key) => headers.set(key, value));
  return headers;
}

/**
 * 保留 Snowplow 创建 Request 时带入的发送语义，避免 gzip 改写时丢失离页保活能力。
 */
function preserveRequestInit(input: RequestInfo | URL, init?: RequestInit): RequestInit {
  if (!(input instanceof Request)) {
    return { ...init };
  }

  return {
    ...init,
    cache: init?.cache ?? input.cache,
    credentials: init?.credentials ?? input.credentials,
    integrity: init?.integrity ?? input.integrity,
    keepalive: init?.keepalive ?? input.keepalive,
    mode: init?.mode ?? input.mode,
    redirect: init?.redirect ?? input.redirect,
    referrer: init?.referrer ?? input.referrer,
    referrerPolicy: init?.referrerPolicy ?? input.referrerPolicy,
    signal: init?.signal ?? input.signal,
  };
}

async function getRequestBodyBytes(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Uint8Array | null> {
  const initBody = await bodyToUint8Array(init?.body);
  if (initBody) return initBody;

  if (!(input instanceof Request) || !input.body) return null;
  const inputBodyBuffer = await input.clone().arrayBuffer();
  return new Uint8Array(inputBodyBuffer);
}

function supportsCompression(): boolean {
  return (
    typeof CompressionStream !== 'undefined' &&
    typeof Blob !== 'undefined' &&
    typeof TextEncoder !== 'undefined' &&
    typeof Response !== 'undefined' &&
    typeof btoa === 'function'
  );
}

async function gzipBytes(bodyBytes: Uint8Array): Promise<Uint8Array> {
  const buffer = new ArrayBuffer(bodyBytes.byteLength);
  new Uint8Array(buffer).set(bodyBytes);
  const stream = new Blob([buffer]).stream().pipeThrough(new CompressionStream('gzip'));
  const compressedBuffer = await new Response(stream).arrayBuffer();
  return new Uint8Array(compressedBuffer);
}

export function calcCrc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (let index = 0; index < bytes.length; index += 1) {
    const byte = bytes[index] ?? 0;
    crc = CRC32_TABLE[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

export function formatCrc32(crc: number): string {
  return crc.toString(16).padStart(8, '0');
}

export function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunkSize = 0x8000;

  for (let index = 0; index < bytes.length; index += chunkSize) {
    const chunk = bytes.subarray(index, index + chunkSize);
    for (let chunkIndex = 0; chunkIndex < chunk.length; chunkIndex += 1) {
      binary += String.fromCharCode(chunk[chunkIndex] ?? 0);
    }
  }

  return btoa(binary);
}

export function createSnowplowGzipFetch(
  baseFetch: SnowplowFetch,
  deps: Partial<SnowplowGzipFetchDeps> = {},
): SnowplowFetch {
  const resolvedDeps: SnowplowGzipFetchDeps = {
    supportsCompression,
    compress: gzipBytes,
    encodeBase64: bytesToBase64,
    ...deps,
  };

  return async (input: RequestInfo | URL, init?: RequestInit) => {
    const method = getRequestMethod(input, init);

    if (method !== 'POST' || !resolvedDeps.supportsCompression()) {
      return baseFetch(input, init);
    }

    try {
      const bodyBytes = await getRequestBodyBytes(input, init);
      if (!bodyBytes || bodyBytes.length === 0) return baseFetch(input, init);

      const gzipData = await resolvedDeps.compress(bodyBytes);
      const crcValue = formatCrc32(calcCrc32(gzipData));
      const payload = new URLSearchParams({
        crc: crcValue,
        gzip: '1',
        data: resolvedDeps.encodeBase64(gzipData),
      }).toString();

      const headers = mergeHeaders(input, init);
      headers.set('Content-Type', 'application/x-www-form-urlencoded');

      return baseFetch(input, {
        ...preserveRequestInit(input, init),
        method,
        headers,
        body: payload,
      });
    } catch {
      return baseFetch(input, init);
    }
  };
}
